import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useCurrentCompany } from './useCurrentCompany';

export interface Payment {
  id: string;
  memberName: string;
  subscriptionName: string;
  amount: number;
  date: string;
  method: string;
  status: 'succeeded' | 'failed' | 'pending' | 'refunded';
  declineReason?: string;
  created_at: string;
}

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  stripe: 'RollOps',
  cash: 'Cash',
  other: 'Other',
};

function formatPaymentMethod(method: string): string {
  const lower = (method || '').toLowerCase();
  return PAYMENT_METHOD_LABELS[lower] || method || 'Other';
}

function formatDate(isoDate: string): string {
  const d = new Date(isoDate);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export interface UsePaymentsParams {
  limit?: number;
  offset?: number;
  enabled?: boolean;
}

export interface UsePaymentsResult {
  payments: Payment[];
  hasNextPage: boolean;
}

interface PaymentRecordRow {
  id: string;
  amount: number;
  billing_date: string;
  created_at: string;
  paid_at: string | null;
  failed_at: string | null;
  payment_method: string | null;
  status: string;
  enrollment_id: string | null;
  subscription_id: string | null;
  student_user_id: string | null;
  child_profile_id: string | null;
  metadata: Record<string, unknown> | null;
}

/**
 * Fetches payments for the current company.
 * Used for both Recent Payments (BillingUnified) and Payment History (BillingPayments).
 * Only includes enrollment-based payments (gym member subscriptions), not SaaS subscription payments.
 * Supports pagination via offset; when offset is used, fetches limit+1 to determine hasNextPage.
 */
export function usePayments({
  limit = 50,
  offset = 0,
  enabled = true,
}: UsePaymentsParams = {}) {
  const { data: companyId } = useCurrentCompany();

  return useQuery({
    queryKey: ['payments', companyId, limit, offset],
    enabled: !!companyId && enabled,
    queryFn: async (): Promise<UsePaymentsResult> => {
      if (!companyId) return { payments: [], hasNextPage: false };

      const fetchCount = limit + 1; // Fetch one extra to detect if there's a next page
      const { data, error: paymentsError } = await supabase
        .from('payment_records')
        .select('id, amount, billing_date, created_at, paid_at, failed_at, payment_method, status, enrollment_id, subscription_id, student_user_id, child_profile_id, metadata')
        .eq('company_id', companyId)
        .in('status', ['succeeded', 'failed'])
        .eq('payment_type', 'gym_subscription')
        .order('created_at', { ascending: false })
        .range(offset, offset + fetchCount - 1);

      const paymentRecords = data as PaymentRecordRow[] | null;

      if (paymentsError || !paymentRecords?.length) {
        return { payments: [], hasNextPage: false };
      }

      const hasNextPage = paymentRecords.length > limit;
      const recordsToProcess = paymentRecords.slice(0, limit);

      const uniqueEnrollmentIds = [
        ...new Set(
          recordsToProcess
            .map((p: PaymentRecordRow) => p.enrollment_id)
            .filter((id): id is string => id != null),
        ),
      ];

      // For failed payments without enrollment_id, resolve names via subscription_id + student_user_id
      const uniqueSubscriptionIds = [
        ...new Set(
          recordsToProcess
            .filter((p) => !p.enrollment_id && p.subscription_id)
            .map((p) => p.subscription_id)
            .filter((id): id is string => id != null),
        ),
      ];
      const uniqueStudentUserIds = [
        ...new Set(
          recordsToProcess
            .filter((p) => !p.enrollment_id && p.student_user_id)
            .map((p) => p.student_user_id)
            .filter((id): id is string => id != null),
        ),
      ];
      const uniqueChildProfileIds = [
        ...new Set(
          recordsToProcess
            .filter((p) => !p.enrollment_id && p.child_profile_id)
            .map((p) => p.child_profile_id)
            .filter((id): id is string => id != null),
        ),
      ];

      const { data: enrollments } =
        uniqueEnrollmentIds.length > 0
          ? await supabase
              .from('subscription_enrollments')
              .select(
                `
                id,
                user_id,
                child_id,
                company_subscriptions!subscription_enrollments_subscription_id_fkey (name),
                user_profiles!subscription_enrollments_user_id_fkey (first_name, last_name),
                child_profiles!subscription_enrollments_child_id_fkey (first_name, last_name)
              `,
              )
              .in('id', uniqueEnrollmentIds)
          : { data: [] };

      // Fetch subscription names for failed payments
      const { data: subscriptions } =
        uniqueSubscriptionIds.length > 0
          ? await supabase
              .from('company_subscriptions')
              .select('id, name')
              .in('id', uniqueSubscriptionIds)
          : { data: [] };

      // Fetch student profiles for failed payments
      const { data: studentProfiles } =
        uniqueStudentUserIds.length > 0
          ? await supabase
              .from('user_profiles')
              .select('id, first_name, last_name')
              .in('id', uniqueStudentUserIds)
          : { data: [] };

      // Fetch child profiles for failed payments
      const { data: childProfiles } =
        uniqueChildProfileIds.length > 0
          ? await supabase
              .from('child_profiles')
              .select('id, first_name, last_name')
              .in('id', uniqueChildProfileIds)
          : { data: [] };

      const enrollmentMap = new Map(
        (enrollments || []).map((e: { id: string; [key: string]: unknown }) => [e.id, e]),
      );
      const subscriptionMap = new Map(
        (subscriptions || []).map((s: { id: string; name: string }) => [s.id, s.name]),
      );
      const studentProfileMap = new Map(
        (studentProfiles || []).map((u: { id: string; first_name?: string | null; last_name?: string | null }) => [u.id, u]),
      );
      const childProfileMap = new Map(
        (childProfiles || []).map((c: { id: string; first_name?: string | null; last_name?: string | null }) => [c.id, c]),
      );

      const payments = recordsToProcess.map((pr: PaymentRecordRow) => {
        const enrollment = (pr.enrollment_id
          ? enrollmentMap.get(pr.enrollment_id)
          : undefined) as
          | {
              user_profiles?: { first_name?: string | null; last_name?: string | null } | null;
              child_profiles?: { first_name?: string | null; last_name?: string | null } | null;
              company_subscriptions?: { name?: string | null } | null;
            }
          | undefined;

        let memberName = 'Unknown';
        if (enrollment?.child_profiles) {
          const c = enrollment.child_profiles;
          memberName = `${c.first_name || ''} ${c.last_name || ''}`.trim() || 'Unknown';
        } else if (enrollment?.user_profiles) {
          const u = enrollment.user_profiles;
          memberName = `${u.first_name || ''} ${u.last_name || ''}`.trim() || 'Unknown';
        } else if (pr.child_profile_id && childProfileMap.has(pr.child_profile_id)) {
          const c = childProfileMap.get(pr.child_profile_id)!;
          memberName = `${c.first_name || ''} ${c.last_name || ''}`.trim() || 'Unknown';
        } else if (pr.student_user_id && studentProfileMap.has(pr.student_user_id)) {
          const u = studentProfileMap.get(pr.student_user_id)!;
          memberName = `${u.first_name || ''} ${u.last_name || ''}`.trim() || 'Unknown';
        }

        const subscriptionName =
          (enrollment?.company_subscriptions?.name as string) ||
          (pr.subscription_id ? subscriptionMap.get(pr.subscription_id) : undefined) ||
          (pr.metadata as Record<string, unknown>)?.subscription_name as string ||
          'Subscription';

        const dateForDisplay =
          pr.paid_at || pr.failed_at || pr.created_at || pr.billing_date || new Date().toISOString();

        const declineReason = pr.status === 'failed' && pr.metadata
          ? ((pr.metadata as Record<string, unknown>).error_message as string) || undefined
          : undefined;

        return {
          id: pr.id,
          memberName,
          subscriptionName,
          amount: pr.amount,
          date: formatDate(dateForDisplay),
          method: formatPaymentMethod(pr.payment_method || 'other'),
          status: pr.status as Payment['status'],
          declineReason,
          created_at: pr.created_at,
        };
      });

      return { payments, hasNextPage };
    },
    staleTime: 30 * 1000,
  });
}
